import React, { useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, func, shape, string } from 'prop-types';
import {
  HeroButton,
  HeroContainer,
  HeroDescription,
  HeroMeta,
  HeroMetaBadge,
  HeroMetaRow,
  HeroMetaText,
  HeroNavButton,
  HeroNavControls,
  HeroTitle,
  HeroBadgeRow,
  HeroBadgeIcon,
  HeroInfoIcon,
  HeroButtonRow,
} from './Hero.styles';

const AUTOPLAY_INTERVAL_MS = 8000;
const badgeLabels = {
  TOP_10: 'TOP 10',
  NUMBER_ONE_TODAY: '#1 in TV Shows',
};

const normalizePath = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return path.startsWith('/') ? path : `/${path}`;
};

function Hero({
  movies,
  mode,
  onPlay,
  onMoreInfo,
  heroImageOverride,
  heroImagePosition,
  heroTitleOverride,
  top10IconPath,
}) {
  const items = useMemo(() => movies.filter(Boolean), [movies]);
  const isCarousel = mode === 'carousel' && items.length > 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const playButtonRef = useRef(null);
  const activeMovie = items[activeIndex] || items[0];

  useEffect(() => {
    if (!isCarousel) {
      return undefined;
    }
    const handle = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [isCarousel, items.length]);

  useEffect(() => {
    if (!isCarousel) {
      setActiveIndex(0);
    }
  }, [isCarousel]);

  useEffect(() => {
    if (!playButtonRef.current) return;
    playButtonRef.current.focus();
  }, []);

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <HeroContainer
      backgroundUrl={normalizePath(
        heroImageOverride
          || activeMovie?.backgroundPath
          || activeMovie?.imagePath,
      )}
      $backgroundPosition={heroImagePosition}
    >
      <HeroTitle>{heroTitleOverride || activeMovie?.title || activeMovie?.name}</HeroTitle>
      <HeroMeta>
        <HeroMetaRow>
          {typeof activeMovie?.matchPercent === 'number' ? (
            <HeroMetaText $highlight>
              {activeMovie.matchPercent}
              % Match
            </HeroMetaText>
          ) : null}
          {activeMovie?.year ? (
            <HeroMetaText>{activeMovie.year}</HeroMetaText>
          ) : null}
          {activeMovie?.rating ? (
            <HeroMetaBadge>{activeMovie.rating}</HeroMetaBadge>
          ) : null}
          {activeMovie?.seasonCount ? (
            <HeroMetaText>
              {activeMovie.seasonCount}
              {' '}
              Season
            </HeroMetaText>
          ) : null}
        </HeroMetaRow>
        {Array.isArray(activeMovie?.badges) && activeMovie.badges.length ? (
          <HeroBadgeRow>
            {top10IconPath ? <HeroBadgeIcon src={top10IconPath} alt="Top 10" /> : null}
            <HeroMetaBadge>
              {badgeLabels[activeMovie.badges[0]] || activeMovie.badges[0]}
            </HeroMetaBadge>
          </HeroBadgeRow>
        ) : null}
      </HeroMeta>
      <HeroDescription>
        {activeMovie?.description || activeMovie?.overview}
      </HeroDescription>
      <HeroButtonRow>
        <HeroButton
          ref={playButtonRef}
          type="button"
          onClick={() => {
            if (onPlay) onPlay();
          }}
          $variant="primary"
        >
          ▶
          Play
        </HeroButton>
        <HeroButton
          type="button"
          onClick={() => {
            if (onMoreInfo) onMoreInfo();
          }}
          $variant="secondary"
        >
          <HeroInfoIcon>i</HeroInfoIcon>
          More Info
        </HeroButton>
      </HeroButtonRow>
      {isCarousel ? (
        <HeroNavControls>
          <HeroNavButton type="button" onClick={goPrev} aria-label="Previous">
            &#x2039;
          </HeroNavButton>
          <HeroNavButton type="button" onClick={goNext} aria-label="Next">
            &#x203A;
          </HeroNavButton>
        </HeroNavControls>
      ) : null}
    </HeroContainer>
  );
}

export default Hero;

Hero.propTypes = {
  movies: arrayOf(shape),
  mode: string,
  onPlay: func,
  onMoreInfo: func,
  heroImageOverride: string,
  heroImagePosition: string,
  heroTitleOverride: string,
  top10IconPath: string,
}.isRequired;
